<template>
  <div>
    <app-spinner v-if="!isLoaded"/>
    <template v-else>
      <div class="hm-res">
        <div class="hm-res__top">
          <div class="hm-heading">
            <span class="hm-heading__title"><span class="hm-heading__id">{{ rData.property.name }}</span></span>
            <span class="hm-heading__title">| Reservation <span class="hm-heading__id">#{{ $route.params.id }}</span></span>
          </div>
          <div class="hm-res__controls">
            <div
              v-if="isHotelReception || isGeneralManager || isHotelReservations"
              class="hm-status"
              ref="statusRoot"
            >
              <button
                @click="toggleStatus"
                class="hm-status__btn"
                type="button"
                :style="{ color: currentStatusColor }"
                :disabled="rData.status.available_statuses.length < 1 || isEditDisabled"
              >
                <span class="hm-status__text">{{ rData.status.name }}</span>
                <svg v-if="rData.status.available_statuses.length > 0 && !isEditDisabled" class="hm-status__caret" width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M7 10l5 5 5-5" fill="none" stroke="currentColor" stroke-width="2"/>
                </svg>
              </button>
              <transition name="hm-status-fade">
                <div
                  v-if="isStatusOpen && rData.status.available_statuses && rData.status.available_statuses.length > 0"
                  class="hm-status__dropdown"
                >
                  <div
                    v-for="opt in rData.status.available_statuses"
                    :key="opt.code"
                    class="hm-status__option"
                    :class="{ 'hm-status__option--selected': opt.code === rData.status.code }"
                    :style="{ color: opt.color || '#000' }"
                    @click="onSelectStatus(opt)"
                  >
                    {{ opt.name }}
                  </div>
                </div>
              </transition>
            </div>
            <div v-if="rData.deadline && rData.deadline.date" class="hm-deadline">
              <span class="hm-deadline__label">DEADLINE:</span>
              <span class="hm-deadline__badge" :class="deadlineBadgeClass">
                {{ fmtDate(rData.deadline.date) }}
              </span>
            </div>
          </div>
        </div>
        <div class="hm-res__meta">
          <div class="hm-meta">
            <div class="hm-meta__group">{{ rData.group_name }}</div>
            <div v-if="rData.source && rData.source.name" class="hm-meta__source">
              {{ rData.source.name }}
              <template v-if="rData.external_id">| {{ rData.external_id }}</template>
            </div>
          </div>
        </div>
        <div class="hm-res__tabs">
          <nav class="hm-tabs" aria-label="Reservation sections">
            <router-link
              class="hm-tabs__item"
              exact
              active-class="hm-tabs__item--active"
              :to="{ name: 'hotel-management-reservations-detail-reservation', params: { id: $route.params.id } }"
            >
              <ReservationsIcon class="hm-tabs__icon"/>
              <span class="hm-tabs__text">Reservation</span>
            </router-link>

            <router-link
              class="hm-tabs__item"
              active-class="hm-tabs__item--active"
              :to="{ name: 'hotel-management-reservations-detail-rooms-detail', params: { id: $route.params.id } }"
            >
              <RoomsIcon class="hm-tabs__icon"/>
              <span class="hm-tabs__text">Rooms</span>
            </router-link>

            <router-link
              class="hm-tabs__item"
              active-class="hm-tabs__item--active"
              :to="{ name: 'hotel-management-reservations-detail-invoice', params: { id: $route.params.id } }"
            >
              <InvoiceIcon class="hm-tabs__icon"/>
              <span class="hm-tabs__text">Invoice</span>
            </router-link>
          </nav>
        </div>
      </div>
      <transition name="fade" mode="out-in">
        <router-view @update-data="fetchData" :rData="rData"/>
      </transition>
    </template>
  </div>
</template>

<script>
import { getHotelManagementReservationV2, patchHotelManagementReservationV2 } from '@/http'
import ReservationsIcon from './icons/ReservationsIcon.vue'
import RoomsIcon from './icons/RoomsIcon.vue'
import InvoiceIcon from './icons/InvoiceIcon.vue'

export default {
  name: 'HotelManagementReservationsDetailLayout',
  components: { ReservationsIcon, RoomsIcon, InvoiceIcon },
  data() {
    return {
      isLoaded: false,
      rData: {},
      isStatusOpen: false
    }
  },
  created() {
    this.fetchData()
  },
  computed: {
    is_general_manager() {
      return this.$store.state.userGroups.map(item => item.code).includes('general_manager')
    },
    currentStatusColor() {
      return this.rData.status && this.rData.status.color ? this.rData.status.color : '#000'
    },
    deadlineBadgeClass () {
      const st = (this.rData.deadline && this.rData.deadline.status) || 'ok'
      return `hm-deadline__badge--${st}`
    },
    isHotelReception() {
      return this.$store.state.userGroups.map(item => item.code).includes('hotel_reception')
    },
    isGeneralManager() {
      return this.$store.state.userGroups.map(item => item.code).includes('general_manager')
    },
    isHotelReservations() {
      return this.$store.state.userGroups.map(item => item.code).includes('hotel_reservations')
    },
    isEditDisabled() {
      return this.rData.reservation_hotel !== null
    }
  },
  methods: {
    fmtDate(iso) {
      if (!iso) return ''
      const d = new Date(`${ iso }T00:00:00`)
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      return `${ d.getDate() } ${ months[d.getMonth()] }, ${ d.getFullYear() }`
    },
    fetchData() {
      getHotelManagementReservationV2(this.$route.params.id).then(response => {
        this.rData = response.data
        this.isLoaded = true
      })
    },
    toggleStatus() {
      this.isStatusOpen = !this.isStatusOpen
    },
    onSelectStatus(opt) {
      this.isStatusOpen = false
      if (!opt || opt.code === (
        this.rData.status && this.rData.status.code
      )) return
      patchHotelManagementReservationV2(this.$route.params.id, {status: opt.code})
        .then(() => {
          this.fetchData()
        })
    },
    handleOutsideClick(e) {
      const root = this.$refs.statusRoot
      if (root && !root.contains(e.target)) this.isStatusOpen = false
    },
  },
  mounted() {
    document.addEventListener('click', this.handleOutsideClick)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleOutsideClick)
  }
}
</script>

<style scoped>
.hm-res__top {
  display: flex;
  align-items: center;
  margin-bottom: 30px;
}

.hm-res__meta {
  margin-bottom: 40px;
}

.hm-res__tabs {
  margin-bottom: 50px;
}

.hm-res__controls {
  display: flex;
  align-items: center;
  gap: 16px;
}

.hm-heading {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-right: 40px;
}

.hm-heading__title {
  color: #000;
  font-family: 'MazzardSoftH', sans-serif;
  font-size: 20px;
  font-weight: 400;
  line-height: 1.1;
}

.hm-heading__id {
  color: #000;
  font-family: 'MazzardSoftH', sans-serif;
  font-size: 20px;
  font-weight: 600;
  line-height: 1.1;
}

.hm-status {
  position: relative;
}

.hm-status__btn {
  height: 40px;
  border-radius: 30px;
  border: 1px solid #ECEAE5;
  background: #FCFCFC;
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 0 14px 0 16px;
  font-family: 'MazzardSoftH', sans-serif;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  outline: none;
}

.hm-status__btn:disabled {
  cursor: default;
}

.hm-status__text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.hm-status__caret {
  flex: 0 0 auto;
  opacity: .7;
}

.hm-status__dropdown {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  z-index: 20;
  min-width: 180px;
  padding: 12px;
  border-radius: 16px;
  border: 1px solid #E9E9E9;
  background: #FFF;
  box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.06);
}

.hm-status__option {
  margin-bottom: 5px;
  padding: 6px 10px 4px;
  border-radius: 6px;
  font-family: 'MazzardSoftH', sans-serif;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;

  &:last-child {
    margin-bottom: 0;
  }
}

.hm-status__option:hover,
.hm-status__option--selected {
  background: linear-gradient(180deg, #EEE 0%, #EDEDED 100%);
}

.hm-status__option--selected {
  pointer-events: none;
}

/* Плавное появление списка */
.hm-status-fade-enter-active,
.hm-status-fade-leave-active {
  transition: opacity .12s ease;
}

.hm-status-fade-enter,
.hm-status-fade-leave-to {
  opacity: 0;
}

.hm-deadline {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: 20px;
}

.hm-deadline__label {
  color: #7d7d7d;
  font-family: 'MazzardSoftH', sans-serif;
  font-size: 13px;
  letter-spacing: 0.52px;
  font-weight: 500;
}

.hm-deadline__badge {
  display: inline-flex;
  align-items: center;
  height: 32px;
  padding: 0 12px;
  border-radius: 8px;
  font-family: 'MazzardSoftH', sans-serif;
  font-size: 16px;
  font-weight: 500;
}

.hm-deadline__badge--ok {
  color: #000;
  background: #F6F6F6;
}

.hm-deadline__badge--expiring {
  color: #C88C00;
  background: #FAF3E0;
}

.hm-deadline__badge--expired {
  color: #CE6A54;
  background: #FFF1EC;
}

.hm-meta {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.hm-meta__group {
  color: #000;
  font-family: 'MazzardSoftH', sans-serif;
  font-size: 20px;
  font-weight: 600;
}

.hm-meta__source {
  color: #666;
  font-family: 'CoreSansCR', sans-serif;
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.hm-tabs {
  display: flex;
  align-items: center;
  gap: 20px;
}

.hm-tabs__item {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 15px;
  border-radius: 17px;
  border: 1px solid #ECEAE5;
  background-color: #FCFCFC;
  box-shadow: 0 1px 5px 0 rgba(159, 167, 177, 0.20);
  color: #8A8A8A;
  text-decoration: none;
  font-family: 'CoreSansCR', sans-serif;
  font-size: 15px;
  line-height: 18px;
  transition: color 0.3s;
}

.hm-tabs__item:hover {
  color: #000
}

.hm-tabs__item--active {
  border: 1px solid #A3A3A3;
  color: #000;
  font-weight: 500;

  .hm-tabs__icon {
    filter: none;
  }
}

.hm-tabs__icon {
  filter: grayscale(100%);
}

.hm-tabs__text {
  white-space: nowrap;
}
</style>
